import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Video, ChevronDown, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { COURSES, CATEGORIES } from "../../data/content";
import { formatCOP } from "../../lib/api";

const CourseCard = ({ course, index, onEnroll }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <div
        data-testid={`course-card-${course.id}`}
        className="group rounded-2xl border border-border bg-card overflow-hidden h-full flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(16,185,129,0.12)] hover:border-emerald-500/40"
      >
        <div className="relative h-44 overflow-hidden">
          <img src={course.image} alt={course.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute top-3 left-3 flex gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest bg-emerald-500 text-white rounded-full px-3 py-1">{course.category}</span>
            <span className="text-[10px] font-mono uppercase tracking-widest bg-black/50 backdrop-blur text-white rounded-full px-3 py-1">{course.level}</span>
          </div>
        </div>

        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-display text-xl font-semibold tracking-tight">{course.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mt-2 flex-1">{course.description}</p>

          <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{course.duration}</span>
            <span className="inline-flex items-center gap-1.5"><Video className="w-3.5 h-3.5" />{course.modality}</span>
          </div>

          <button
            data-testid={`syllabus-toggle-${course.id}`}
            onClick={() => setOpen(!open)}
            className="mt-4 flex items-center justify-between text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:opacity-80 transition-opacity"
          >
            Ver temario
            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.ul
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                {course.syllabus.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground py-1.5">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    {s}
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>

          <div className="mt-5 pt-5 border-t border-border flex items-center justify-between">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Inversión</p>
              <p className="font-display font-bold text-lg">{formatCOP(course.price)}</p>
            </div>
            <button
              data-testid={`enroll-button-${course.id}`}
              onClick={() => onEnroll({ course: course.title, plan: "Pago único" })}
              className="rounded-full bg-primary text-primary-foreground text-sm font-semibold px-5 py-2.5 hover:brightness-110 active:scale-95 transition-all"
            >
              Inscribirme
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export const Courses = ({ onEnroll }) => {
  const [filter, setFilter] = useState("Todos");
  const visible = filter === "Todos" ? COURSES : COURSES.filter((c) => c.category === filter);

  return (
    <section id="cursos" data-testid="course-catalog-section" className="py-24 sm:py-32 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase font-mono tracking-[0.28em] text-emerald-600 dark:text-emerald-400 font-semibold mb-4">Catálogo 2026</p>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-xl">
              Cursos que se pagan <span className="font-serif italic text-emerald-500">solos</span>
            </h2>
            <p className="text-sm text-muted-foreground max-w-sm">Cohortes pequeñas, clases en vivo y grabaciones por 12 meses. Elige tu especialización.</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2.5">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`course-filter-${c.toLowerCase().replace(/[\s/]/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${
                  filter === c
                    ? "bg-primary text-primary-foreground border-primary shadow-lg shadow-emerald-500/20"
                    : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((course, i) => (
              <motion.div key={course.id} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.35 }}>
                <CourseCard course={course} index={i} onEnroll={onEnroll} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
